import { wire, api } from "lwc";
import debounce from "lodash/debounce";
import { Maybe, SearchSortKindType } from "@devforce/trailhead-client-gql";
import { hasAccessTokenResolver } from "@devforce/trailhead-client-auth";
import { FormControl } from "tds/formControl";
import { EffectAdapter } from "common/effectAdapter";
import { SharedLabelsAdapter, SharedLabelsAdapterValue } from "lx/sharedLabels";
import { BooleanAttr, attrToBool } from "common/helpers";
import { generateFilterFormControls } from "../util/filterHelper";
import FilterablePage, {
  SearchTabType,
} from "../filterablePage/filterablePage";
import template from "./learningSearchContentPage.html";
import verticalTemplate from "./vertical.html";
import * as locales from "./locales";

export enum PreSelectedContentType {
  Module = "MODULE",
  Project = "PROJECT",
  Trail = "TRAIL",
  Superbadge = "SUPERBADGE",
  Trailmix = "TRAILMIX",
  All = "ALL",
}

const SORT_OPTIONS: Record<SearchSortKindType, string> = {
  [SearchSortKindType.RelevanceType]: "sort_by_relevance",
  [SearchSortKindType.MostPointsType]: "sort_by_points",
  [SearchSortKindType.QuickestType]: "sort_by_quickest",
  [SearchSortKindType.NewestType]: "sort_by_newest",
};

const SORT_OPTIONS_OTHER_TABS: SearchSortKindType[] = [
  SearchSortKindType.RelevanceType,
  SearchSortKindType.NewestType,
];

const SORT_OPTIONS_SEARCH_TABS: Record<SearchTabType, SearchSortKindType[]> = {
  ALL: SORT_OPTIONS_OTHER_TABS,
  LEARNING: Object.keys(SORT_OPTIONS) as SearchSortKindType[],
  COMMUNITY: SORT_OPTIONS_OTHER_TABS,
  DOCUMENTATION: SORT_OPTIONS_OTHER_TABS,
};

type SearchTabOptions = { value: SearchTabType; icon: string };

export default class extends FilterablePage {
  @api documentationTabDisabled: boolean = false;
  @api inPageSearchEnabled: boolean = false;
  @api preSelectedContentType: PreSelectedContentType =
    PreSelectedContentType.All;
  @api headerText: Maybe<string> = null;
  @api descriptionText: Maybe<string> = null;

  private resultCount: number | undefined = undefined;
  private searchBusy = false;
  private _verticalFiltersEnabled = false;
  private _searchTabsEnabled = false;
  private _progressFilterEnabled = false;
  private _filtersState: FormControl[] = [];
  private _searchTabs: SearchTabOptions[] = [
    { value: "ALL", icon: "salesforce1" },
    { value: "LEARNING", icon: "trailhead" },
    { value: "COMMUNITY", icon: "groups" },
    { value: "DOCUMENTATION", icon: "file" },
  ];

  @api
  get verticalFiltersEnabled() {
    return this._verticalFiltersEnabled;
  }

  set verticalFiltersEnabled(value: BooleanAttr) {
    this._verticalFiltersEnabled = attrToBool(value);
  }

  // Note: this is not just a feature flag that can be removed,
  // as index page components require this to be false at all times.
  // Do not remove during feature-flag removal.
  @api
  get searchTabsEnabled() {
    return this._searchTabsEnabled;
  }

  set searchTabsEnabled(value: BooleanAttr) {
    this._searchTabsEnabled = attrToBool(value);
  }

  // Note: this is to enable the progress filter for index pages
  // until the progress filter configuration is moved to the
  // experience api
  @api
  get progressFilterEnabled() {
    return this._progressFilterEnabled;
  }

  set progressFilterEnabled(value: BooleanAttr) {
    this._progressFilterEnabled = attrToBool(value);
  }

  override setup() {
    if (this.preSelectedContentType !== PreSelectedContentType.All) {
      this.defaultFilters = { content_type: [this.preSelectedContentType] };
    }
    if (this.searchTabsEnabled && this.searchTab == null) {
      // If enabled and no tab set by a URL parameter, default to the LEARNING tab.
      this.searchTab = "LEARNING";
      const url = new URL(window.location.href);
      url.searchParams.set("tab", this.searchTab);
      window.history.pushState({}, "", url.href);
    }
    if (!this.searchTabsEnabled && this.searchTab != null) {
      // If disabled and a tab set by a URL parameter, ignore it.
      this.searchTab = null;
      const url = new URL(window.location.href);
      url.searchParams.delete("tab");
      window.history.pushState({}, "", url.href);
    }
  }

  override get filterDirection() {
    return this._verticalFiltersEnabled ? "vertical" : super.filterDirection;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.documentationTabDisabled) {
      this._searchTabs = this._searchTabs.filter(
        (item) => item.value !== "DOCUMENTATION"
      );
    }
    this.initialSortValue = Object.keys(SORT_OPTIONS)[0] as SearchSortKindType;
  }

  /**
   * Templates
   */

  render() {
    return this.verticalFiltersEnabled ? verticalTemplate : template;
  }

  /**
   * Wire Adapters
   **/

  // Observe changes to the FilterablePage's loading variable
  @wire(EffectAdapter, {
    loading: "$loading",
  })
  private responseReady() {
    if (!this.loading) {
      this.excludeFilterTypes(["tags"]);

      if (this.preSelectedContentType !== PreSelectedContentType.All) {
        this.excludeFilterTypes(["content_type"]);
      }

      if (this.preSelectedContentType === PreSelectedContentType.Trail) {
        this.excludeFilterTypes(["assessments_include_hoc"]);
      }

      if (
        this.preSelectedContentType === PreSelectedContentType.Module ||
        this.preSelectedContentType === PreSelectedContentType.Project
      ) {
        this.excludeFilterTypes(["intent"]);
      }

      this._filtersState = generateFilterFormControls(
        this.filterResponse,
        this.filters,
        this.disabledFilters,
        this.showProgressFilter()
      );
    }
  }

  @wire(EffectAdapter, {
    filters: "$filters",
  })
  private processUpdatedFilters() {
    this._filtersState = generateFilterFormControls(
      this.filterResponse,
      this.filters,
      this.disabledFilters,
      this.showProgressFilter()
    );
  }

  @wire(SharedLabelsAdapter, { locales, options: "$labelOptions" })
  private labels: SharedLabelsAdapterValue = null!;

  private get labelOptions() {
    return {
      num_results_with_query_zero: {
        query: this.queryString,
      },
      num_results_with_query_one: {
        query: this.queryString,
      },
      num_results_with_query_many: {
        count: this.resultCount && this.resultCount.toLocaleString(),
        query: this.queryString,
      },
      num_results_without_query_many: {
        count: this.resultCount && this.resultCount.toLocaleString(),
      },
    };
  }

  /**
   * Event Handlers
   **/

  // == Shared Template Handlers ==

  handleCountChange(e: CustomEvent) {
    this.resultCount = e.detail || 0;
  }

  // == Vertical Filters Handlers ==

  private forwardSearch(e: CustomEvent) {
    this.searchBusy = true;
    this.debounceSearchEvent(e);
  }

  /**
   * Template Methods
   * ---------------------
   * These methods are used in the template only and are not called from the
   * component's JavaScript code.
   * ---------------------
   **/

  // == Vertical Filters ==

  private get filtersState() {
    return this._filtersState;
  }

  private get queryResultLabel() {
    if (this.resultCount === undefined) return "";

    if (this.queryString !== "") {
      if (this.resultCount === 0) {
        return this.labels.num_results_with_query_zero;
      } else if (this.resultCount === 1) {
        return this.labels.num_results_with_query_one;
      } else {
        return this.labels.num_results_with_query_many;
      }
    } else {
      if (this.resultCount === 0) {
        return this.labels.num_results_without_query_zero;
      } else if (this.resultCount === 1) {
        return this.labels.num_results_without_query_one;
      } else {
        return this.labels.num_results_without_query_many;
      }
    }
  }

  private get sortState() {
    const sortOptions = this.searchTabsEnabled
      ? SORT_OPTIONS_SEARCH_TABS[this.searchTab!]
      : (Object.keys(SORT_OPTIONS) as SearchSortKindType[]);

    return {
      id: "sort",
      type: "combobox",
      ariaLabel: this.labels.shared.sort_by,
      value: this.sortType || this.initialSortValue,
      options: sortOptions.map((sortKind) => ({
        label: this.labels.shared[SORT_OPTIONS[sortKind]],
        value: sortKind,
      })),
    };
  }

  get searchTabLabelsReady() {
    return (
      this.labels &&
      this._searchTabs.every(
        (tab) => this.labels[`search_tabs.${tab.value.toLowerCase()}`]
      )
    );
  }

  private get searchTabOptions() {
    return this._searchTabs.map((tab) => ({
      value: tab.value,
      label: this.labels[`search_tabs.${tab.value.toLowerCase()}`],
      icon: { symbol: tab.icon },
    }));
  }

  /**
   * Private Utility Methods
   */

  private debounceSearchEvent = debounce((e: CustomEvent) => {
    this.handleSearch(e, true);
    this.searchBusy = false;
  }, 1000);

  // show progress filter if the following conditions are met
  // 1. user is authenticated
  // 2. preselected content type is not trail
  // 3. vertical filter is enabled
  // 4. progressFilterEnabled or search tabs enabled
  // 5. search tab is either null or set to learning
  private showProgressFilter() {
    return (
      hasAccessTokenResolver() &&
      this.preSelectedContentType !== "TRAIL" &&
      this._verticalFiltersEnabled &&
      (this._searchTabsEnabled || this._progressFilterEnabled) &&
      (this.searchTab === null || this.searchTab === "LEARNING")
    );
  }
}
